import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from './ThemeContext';
const PreferencesContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_PREFERENCES = {
  theme: 'light',
  layout: 'grid',
  typography: 'medium',
  accent_color: '#002147',
  background_image: null,
  custom_font_size: 16
};
const TYPOGRAPHY_SIZES = {
  small: 14,
  medium: 16,
  large: 18
};

// localStorage rehydration so first paint applies the user's saved
// theme/typography/accent BEFORE the network round-trip completes.
const PREFS_CACHE_KEY = 'preferences_cache_v1';
const readCachedPreferences = () => {
  try {
    const raw = localStorage.getItem(PREFS_CACHE_KEY);
    return raw ? { ...DEFAULT_PREFERENCES, ...JSON.parse(raw) } : DEFAULT_PREFERENCES;
  } catch {
    return DEFAULT_PREFERENCES;
  }
};
const writeCachedPreferences = (prefs) => {
  try {
    if (prefs) localStorage.setItem(PREFS_CACHE_KEY, JSON.stringify(prefs));
    else localStorage.removeItem(PREFS_CACHE_KEY);
  } catch {}
};

export const PreferencesProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(readCachedPreferences);
  const [loading, setLoading] = useState(true);
  const { setThemeMode } = useTheme();
  const hasSyncedThemeOnceRef = useRef(false);
  // Axios withCredentials is set globally; no manual auth headers needed
  const loadPreferences = useCallback(async () => {
    // Skip the call entirely on public pages where there's no token —
    // avoids 401 console noise for anonymous visitors on marketing pages.
    if (!localStorage.getItem('auth_token')) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/preferences`);
      setPreferences(response.data);
      writeCachedPreferences(response.data);
    } catch (error) {
      // Network/auth error: keep last-known cached preferences (already
      // hydrated into state) instead of resetting to defaults — avoids
      // a flash of wrong theme on transient 429/5xx.
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);
  const applyPreferences = useCallback(() => {
    const root = document.documentElement;
    const hexToHslTriplet = (hex) => {
      const clean = (hex || '').replace('#', '').trim();
      if (clean.length !== 6) return null;
      const r = parseInt(clean.slice(0, 2), 16) / 255;
      const g = parseInt(clean.slice(2, 4), 16) / 255;
      const b = parseInt(clean.slice(4, 6), 16) / 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const delta = max - min;
      let h = 0;
      if (delta !== 0) {
        if (max === r) h = ((g - b) / delta) % 6;
        else if (max === g) h = (b - r) / delta + 2;
        else h = (r - g) / delta + 4;
        h = Math.round(h * 60);
        if (h < 0) h += 360;
      }
      const l = (max + min) / 2;
      const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
      const hh = Math.round(h);
      const ss = Math.round(s * 100);
      const ll = Math.round(l * 100);
      return `${hh} ${ss}% ${ll}%`;
    };
    const primaryHsl = hexToHslTriplet(preferences.accent_color) || '150 59% 20%';
    const primaryLightness = parseInt(primaryHsl.split(' ')[2], 10);
    const onPrimary = Number.isFinite(primaryLightness) && primaryLightness > 60 ? '0 0% 0%' : '0 0% 100%';
    // Theme is owned by ThemeContext (it writes localStorage + html.classList).
    // We only push preferences.theme into ThemeContext on INITIAL load — once
    // the user toggles, the toggle handler already calls setThemeMode and we
    // must NOT echo it back from here, because the API round-trip may return
    // stale data and clobber the user's just-flipped choice.
    if (!hasSyncedThemeOnceRef.current && preferences.theme) {
      setThemeMode(preferences.theme);
      hasSyncedThemeOnceRef.current = true;
    }
    // Apply accent color (shadcn uses HSL triplets in CSS variables)
    root.style.setProperty('--accent-color', preferences.accent_color);
    root.style.setProperty('--primary', primaryHsl);
    root.style.setProperty('--ring', primaryHsl);
    root.style.setProperty('--accent', primaryHsl);
    root.style.setProperty('--primary-foreground', onPrimary);
    root.style.setProperty('--accent-foreground', onPrimary);
    // Apply typography
    const fontSize = TYPOGRAPHY_SIZES[preferences.typography] || preferences.custom_font_size || 16;
    root.style.setProperty('--base-font-size', `${fontSize}px`);
    document.body.style.fontSize = `${fontSize}px`;
    // Apply background image
    if (preferences.background_image) {
      const bgPath = preferences.background_image;
      const bgUrl = bgPath.startsWith('/uploads/')
        ? `${BACKEND_URL}/api${bgPath}`
        : `${BACKEND_URL}${bgPath}`;
      document.body.style.backgroundImage = `url(${bgUrl})`;
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundAttachment = 'fixed';
    } else {
      document.body.style.backgroundImage = 'none';
    }
    // Apply layout preference (stored for components to use)
    localStorage.setItem('layout_preference', preferences.layout);
  }, [preferences, setThemeMode]);
  useEffect(() => {
    applyPreferences();
  }, [applyPreferences]);
  const updatePreference = async (key, value) => {
    try {
      const updates = { [key]: value };
      const response = await axios.put(`${API}/preferences`, updates);
      setPreferences(response.data);
      writeCachedPreferences(response.data);
      toast.success('Preference updated');
      return response.data;
    } catch (error) {
      console.error('Error updating preference:', error);
      toast.error('Failed to update preference');
      throw error;
    }
  };
  const updateMultiplePreferences = async (updates) => {
    try {
      const response = await axios.put(`${API}/preferences`, updates);
      setPreferences(response.data);
      writeCachedPreferences(response.data);
      toast.success('Preferences updated');
      return response.data;
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update preferences');
      throw error;
    }
  };
  const uploadBackground = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/preferences/background-upload`, formData, {
        headers: {
          
          'Content-Type': 'multipart/form-data'
        }
      });
      // Reload preferences to get the new background
      await loadPreferences();
      toast.success('Background uploaded successfully');
      return response.data;
    } catch (error) {
      console.error('Error uploading background:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload background');
      throw error;
    }
  };
  const removeBackground = async () => {
    try {
      await axios.delete(`${API}/preferences/background`);
      await loadPreferences();
      toast.success('Background removed');
    } catch (error) {
      console.error('Error removing background:', error);
      toast.error('Failed to remove background');
      throw error;
    }
  };
  const resetToDefaults = async () => {
    try {
      const response = await axios.post(`${API}/preferences/reset`, null);
      setPreferences(response.data.preferences);
      writeCachedPreferences(response.data.preferences);
      toast.success('Preferences reset to defaults');
      return response.data.preferences;
    } catch (error) {
      console.error('Error resetting preferences:', error);
      toast.error('Failed to reset preferences');
      throw error;
    }
  };
  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        loading,
        updatePreference,
        updateMultiplePreferences,
        uploadBackground,
        removeBackground,
        resetToDefaults,
        loadPreferences
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};
export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};